import type { PublicHeroAction } from './PublicMarketing';

export type PublicUseCasePoint = {
  title: string;
  body: string;
};

export type PublicUseCaseCapability = PublicUseCasePoint & {
  items: readonly string[];
};

export type PublicUseCaseOutcome = {
  label: string;
  value: string;
  body: string;
};

export type PublicUseCaseFaq = {
  question: string;
  answer: string;
};

export type PublicUseCasePageContent = {
  slug: string;
  path: string;
  title: string;
  navLabel: string;
  description: string;
  imageAlt: string;
  eyebrow: string;
  hero: {
    title: string;
    summary: string;
    primaryAction: PublicHeroAction;
    secondaryAction?: PublicHeroAction;
  };
  problem: {
    title: string;
    intro: string;
    points: readonly PublicUseCasePoint[];
  };
  solution: {
    title: string;
    intro: string;
    points: readonly PublicUseCasePoint[];
  };
  capabilities: readonly PublicUseCaseCapability[];
  outcomes: readonly PublicUseCaseOutcome[];
  faq: readonly PublicUseCaseFaq[];
};

const sharedPrimaryAction = {
  href: '/preview',
  label: 'Open Preview',
  variant: 'primary',
} as const satisfies PublicHeroAction;

const sharedSecondaryAction = {
  href: '/features',
  label: 'Review Features',
  variant: 'secondary',
} as const satisfies PublicHeroAction;

export const publicUseCasePages = [
  {
    slug: 'who-is-flowqa-for',
    path: '/use-cases/who-is-flowqa-for',
    title: 'Who Is FlowQA For? | FlowQA',
    navLabel: 'Who FlowQA Is For',
    description:
      'FlowQA is for QA, engineering, release, and product teams that need shared visibility over test history, quality trends, reporting, and spreadsheet-based results.',
    imageAlt: 'FlowQA audience fit for QA engineering and release teams',
    eyebrow: 'Use case',
    hero: {
      title: 'FlowQA is for teams that need test quality visibility',
      summary:
        'FlowQA helps QA, engineering, release, and product teams turn scattered test output into shared quality history, reporting, and trend visibility.',
      primaryAction: sharedPrimaryAction,
      secondaryAction: sharedSecondaryAction,
    },
    problem: {
      title: 'Different teams need the same quality picture',
      intro:
        'Test results are often created in one workflow, investigated in another, and reported somewhere else.',
      points: [
        {
          title: 'QA teams need history',
          body: 'QA needs to know whether failures are new, recurring, manually verified, or part of a longer quality trend.',
        },
        {
          title: 'Engineering needs signal',
          body: 'Engineering needs enough context to separate genuine regressions from noisy output, flaky tests, and expired logs.',
        },
        {
          title: 'Leaders need clarity',
          body: 'Release, product, and delivery stakeholders need a clear quality story without reading build output or chasing spreadsheets.',
        },
      ],
    },
    solution: {
      title: 'FlowQA gives each team one shared review surface',
      intro:
        'The same stored results can support investigation, reporting, trend review, and stakeholder communication.',
      points: [
        {
          title: 'For QA and test leads',
          body: 'Track manual and automated results over time, apply tags, and review recurring issues without rebuilding the evidence trail.',
        },
        {
          title: 'For engineering teams',
          body: 'Use runs, suites, tests, durations, failures, and flake signals to decide which quality problems need attention.',
        },
        {
          title: 'For delivery stakeholders',
          body: 'Review meeting-ready quality status from a product UI rather than asking teams to prepare bespoke summaries.',
        },
      ],
    },
    capabilities: [
      {
        title: 'QA operations',
        body: 'Support teams that need durable test history across manual and automated sources.',
        items: ['Historical runs', 'Manual imports', 'Tags'],
      },
      {
        title: 'Engineering quality',
        body: 'Give engineers practical signals for investigating failures and quality movement.',
        items: ['Pass rate', 'Duration trends', 'Failure recurrence'],
      },
      {
        title: 'Release reporting',
        body: 'Help delivery teams explain quality status without assembling reports from logs and files.',
        items: ['Stakeholder summaries', 'Project views', 'Evidence drilldowns'],
      },
    ],
    outcomes: [
      {
        label: 'Align',
        value: 'One quality view',
        body: 'Let QA, engineering, and delivery teams work from the same stored result history.',
      },
      {
        label: 'Explain',
        value: 'Clear status',
        body: 'Make quality conversations easier without asking every team to prepare a bespoke report.',
      },
      {
        label: 'Scale',
        value: 'Across sources',
        body: 'Bring CI output, spreadsheets, and manual records into a shared product surface.',
      },
    ],
    faq: [
      {
        question: 'Is FlowQA only for QA teams?',
        answer:
          'No. QA teams are a natural starting point, but FlowQA is also useful for engineering, release, delivery, and product stakeholders who need shared test visibility.',
      },
      {
        question: 'Is FlowQA useful if a team already has CI?',
        answer:
          'Yes. CI runs tests, while FlowQA keeps the results easier to review, compare, and report after the build has finished.',
      },
      {
        question: 'Can FlowQA support manual testing teams?',
        answer:
          'Yes. CSV and spreadsheet import support helps manual results become part of the same quality history as automated output.',
      },
    ],
  },
  {
    slug: 'test-results-history',
    path: '/use-cases/test-results-history',
    title: 'Test Results History | FlowQA',
    navLabel: 'Test Results History',
    description:
      'Track test results over time with FlowQA so teams can review historical pass rates, failures, durations, tags, and imported evidence instead of relying on latest CI logs.',
    imageAlt: 'FlowQA test results history and quality trends',
    eyebrow: 'Use case',
    hero: {
      title: 'Track test results over time, not just the latest run',
      summary:
        'FlowQA keeps every imported run reviewable so teams can see whether test health is improving, degrading, or repeating the same issues across builds.',
      primaryAction: sharedPrimaryAction,
      secondaryAction: sharedSecondaryAction,
    },
    problem: {
      title: 'Latest results are not enough',
      intro:
        'Most test workflows make the newest CI output easy to find and everything before it harder to trust.',
      points: [
        {
          title: 'Build logs move on',
          body: 'A failed test may be visible today, then buried behind new jobs, expired logs, or raw XML files tomorrow.',
        },
        {
          title: 'Trends stay hidden',
          body: 'Teams can see the current red or green status, but not whether a suite has been quietly improving or getting worse.',
        },
        {
          title: 'Repeated failures get rediscovered',
          body: 'Without durable history, the same recurring failure can be investigated as if it were new each time.',
        },
      ],
    },
    solution: {
      title: 'FlowQA turns runs into history',
      intro:
        'Upload results from existing tools and review them through a stable product surface built for comparison over time.',
      points: [
        {
          title: 'Store each run',
          body: 'Keep execution records, suite outcomes, and test-level evidence available after CI output stops being useful.',
        },
        {
          title: 'Compare outcomes',
          body: 'Review pass rate, failure recurrence, duration, and flake signals beside the records that produced them.',
        },
        {
          title: 'Find the test again',
          body: 'Use project history, suite drilldowns, and tags instead of memorising where a result appeared in build output.',
        },
      ],
    },
    capabilities: [
      {
        title: 'Historical run records',
        body: 'Review imported runs as a timeline rather than a disposable build artifact.',
        items: ['Execution history', 'Suite outcomes', 'Test records'],
      },
      {
        title: 'Quality movement',
        body: 'See whether key signals are improving or degrading across the same stored result set.',
        items: ['Pass rate', 'Duration drift', 'Failure recurrence'],
      },
      {
        title: 'Searchable context',
        body: 'Keep the result details, source, and tags attached to the record you need later.',
        items: ['Tags', 'Imported evidence', 'Project context'],
      },
    ],
    outcomes: [
      {
        label: 'Review',
        value: 'One history',
        body: 'Give engineering and QA one place to inspect how tests changed over time.',
      },
      {
        label: 'Evidence',
        value: 'Less log digging',
        body: 'Use stored records instead of reconstructing history from CI output.',
      },
      {
        label: 'Focus',
        value: 'Clear repeaters',
        body: 'Spot tests that keep coming back so recurring failures stop blending into noise.',
      },
    ],
    faq: [
      {
        question: 'Does FlowQA replace CI?',
        answer:
          'No. CI still runs the tests. FlowQA keeps the results searchable, comparable, and easier to review after the job has finished.',
      },
      {
        question: 'Can this work with existing test output?',
        answer:
          'Yes. The goal is to preserve results from workflows teams already use rather than forcing every team into a new runner.',
      },
      {
        question: 'Why is historical tracking useful?',
        answer:
          'It helps teams separate new regressions from recurring issues and see whether test quality is improving over time.',
      },
    ],
  },
  {
    slug: 'test-quality-metrics',
    path: '/use-cases/test-quality-metrics',
    title: 'Test Quality Metrics | FlowQA',
    navLabel: 'Quality Metrics',
    description:
      'Use FlowQA quality metrics to understand pass rate, duration, failure recurrence, flake signals, and test health movement across projects.',
    imageAlt: 'FlowQA test quality metrics and trend reporting',
    eyebrow: 'Use case',
    hero: {
      title: 'See where test quality is improving or degrading',
      summary:
        'FlowQA turns stored test results into practical quality signals so teams can understand movement over time instead of reacting to isolated failures.',
      primaryAction: sharedPrimaryAction,
      secondaryAction: sharedSecondaryAction,
    },
    problem: {
      title: 'Status alone does not explain quality',
      intro:
        'A green build can hide slow tests, frequent retries, and suites that are trending in the wrong direction.',
      points: [
        {
          title: 'Quality gets flattened',
          body: 'Pass or fail is useful, but it rarely tells teams whether reliability, duration, or recurrence is changing.',
        },
        {
          title: 'Direction is hard to see',
          body: 'Teams need trend signals that support DORA-style continuous improvement habits without building bespoke reports.',
        },
        {
          title: 'Action is unclear',
          body: 'Without metrics, it is difficult to decide which tests need ownership, attention, or acceptance as known noise.',
        },
      ],
    },
    solution: {
      title: 'FlowQA gives test health direction',
      intro:
        'Quality metrics sit beside the underlying run evidence, so teams can move from signal to detail quickly.',
      points: [
        {
          title: 'Measure the pattern',
          body: 'Pass rate, duration, frequency, recurrence, and flake signals make quality movement visible.',
        },
        {
          title: 'Keep metrics grounded',
          body: 'Every signal is connected to stored runs, suites, and tests rather than detached dashboard numbers.',
        },
        {
          title: 'Review by project',
          body: 'Teams can compare quality signals across projects while still drilling into the result that changed.',
        },
      ],
    },
    capabilities: [
      {
        title: 'Outcome metrics',
        body: 'Understand how often suites and tests pass, fail, or repeat known problems.',
        items: ['Pass rate', 'Failure recurrence', 'Status history'],
      },
      {
        title: 'Performance signals',
        body: 'Spot slow or drifting tests before runtime changes become accepted background noise.',
        items: ['Duration trends', 'Run frequency', 'Suite movement'],
      },
      {
        title: 'Flake visibility',
        body: 'Use repeated pass/fail movement as a quality signal instead of treating every failure the same.',
        items: ['Flake score', 'Intermittent results', 'Trend review'],
      },
    ],
    outcomes: [
      {
        label: 'Direction',
        value: 'Improving or degrading',
        body: 'Review whether test health is moving in the right direction.',
      },
      {
        label: 'Prioritise',
        value: 'Signal first',
        body: 'Use metrics to decide which suites need attention before reading raw output.',
      },
      {
        label: 'Report',
        value: 'Evidence-backed',
        body: 'Use clear metrics in quality conversations without maintaining custom dashboards.',
      },
    ],
    faq: [
      {
        question: 'Are these DORA metrics?',
        answer:
          'They are test-quality signals inspired by the same improvement mindset. FlowQA does not replace DORA metrics; it gives test teams practical health signals for their result history.',
      },
      {
        question: 'Can metrics be traced back to results?',
        answer:
          'Yes. The value is that metrics stay close to the runs, suites, and tests that created them.',
      },
      {
        question: 'Do teams need to build reports first?',
        answer:
          'No. FlowQA is designed to surface the core signals directly from imported result history.',
      },
    ],
  },
  {
    slug: 'test-reporting',
    path: '/use-cases/test-reporting',
    title: 'Test Reporting | FlowQA',
    navLabel: 'Test Reporting',
    description:
      'Create meeting-ready test reporting with FlowQA by turning CI logs, XML files, CSVs, and spreadsheets into a clear shared quality view.',
    imageAlt: 'FlowQA test reporting for stakeholders and QA teams',
    eyebrow: 'Use case',
    hero: {
      title: 'Make test reporting useful without bespoke report work',
      summary:
        'FlowQA presents test outcomes in a polished product view so teams can show what passed, what failed, and what changed without digging through logs.',
      primaryAction: sharedPrimaryAction,
      secondaryAction: sharedSecondaryAction,
    },
    problem: {
      title: 'Raw output is not a report',
      intro:
        'CI logs and XML files help machines, but they do not make quality status easy to discuss with teams or stakeholders.',
      points: [
        {
          title: 'Build output is unfriendly',
          body: 'The information exists, but it is usually buried in job pages, XML files, or tool-specific output.',
        },
        {
          title: 'Reports take manual work',
          body: 'Teams often rebuild the same summary in spreadsheets or slides before every quality conversation.',
        },
        {
          title: 'Stakeholders need context',
          body: 'A pass/fail snapshot is not enough when people need to understand recurring issues and trend direction.',
        },
      ],
    },
    solution: {
      title: 'FlowQA turns results into a shared view',
      intro:
        'Imported results are normalised into a clear interface for review, filtering, and meeting-ready discussion.',
      points: [
        {
          title: 'One quality surface',
          body: 'Review pass, fail, and trend information without jumping between build systems and file exports.',
        },
        {
          title: 'Minimal tweaking',
          body: 'Use the product view directly instead of repeatedly shaping bespoke spreadsheet reports.',
        },
        {
          title: 'Meeting-ready evidence',
          body: 'Move from a summary to the run, suite, or test record behind a question.',
        },
      ],
    },
    capabilities: [
      {
        title: 'Readable status',
        body: 'Present results in a UI designed for scanning and comparison.',
        items: ['Passed tests', 'Failed tests', 'Recent changes'],
      },
      {
        title: 'Drilldown paths',
        body: 'Move from high-level status into the result detail when a question comes up.',
        items: ['Runs', 'Suites', 'Tests'],
      },
      {
        title: 'Shareable context',
        body: 'Use a consistent product view across QA, engineering, and delivery conversations.',
        items: ['Projects', 'Tags', 'Quality metrics'],
      },
    ],
    outcomes: [
      {
        label: 'Meetings',
        value: 'Less prep',
        body: 'Reduce the manual work needed to produce a credible quality update.',
      },
      {
        label: 'Clarity',
        value: 'One view',
        body: 'Give stakeholders a readable status view without sending them to raw build logs.',
      },
      {
        label: 'Trust',
        value: 'Traceable',
        body: 'Keep summaries connected to the underlying result evidence.',
      },
    ],
    faq: [
      {
        question: 'Can FlowQA replace custom spreadsheets for reporting?',
        answer:
          'For recurring quality status, yes. Spreadsheets can still be imported as source data, but the report should live in FlowQA.',
      },
      {
        question: 'Is this only for QA teams?',
        answer:
          'No. QA may own the workflow, but the view is useful for engineering, delivery, and product stakeholders.',
      },
      {
        question: 'Can users still inspect the detailed result?',
        answer:
          'Yes. The reporting view should make it easy to move into the run, suite, or test behind a status.',
      },
    ],
  },
  {
    slug: 'spreadsheet-test-management',
    path: '/use-cases/spreadsheet-test-management',
    title: 'Spreadsheet Test Management | FlowQA',
    navLabel: 'Spreadsheet Replacement',
    description:
      'Replace emailed test spreadsheets with FlowQA by importing CSV and XLSX results, mapping headers, tagging records, and keeping manual test history searchable.',
    imageAlt: 'FlowQA spreadsheet test management and CSV import',
    eyebrow: 'Use case',
    hero: {
      title: 'Stop passing test spreadsheets around',
      summary:
        'FlowQA helps teams move spreadsheet-based testing into a searchable quality history while still supporting CSV and XLSX inputs.',
      primaryAction: sharedPrimaryAction,
      secondaryAction: sharedSecondaryAction,
    },
    problem: {
      title: 'Spreadsheets do not scale as quality history',
      intro:
        'Spreadsheet workflows are flexible, but they become difficult to govern once reports are copied, emailed, renamed, and stored in folders.',
      points: [
        {
          title: 'History fragments',
          body: 'Teams end up searching folders and inboxes instead of reviewing a durable test record.',
        },
        {
          title: 'Formats drift',
          body: 'Each team can have slightly different columns, labels, and spreadsheet conventions.',
        },
        {
          title: 'Reporting repeats',
          body: 'Manual summaries are recreated instead of accumulating into a consistent quality view.',
        },
      ],
    },
    solution: {
      title: 'FlowQA turns files into records',
      intro:
        'CSV and spreadsheet imports can feed the same review surface as automated results, with mapping and tags preserving context.',
      points: [
        {
          title: 'Import manual sources',
          body: 'Bring CSV and XLSX results into FlowQA so spreadsheet evidence becomes part of product history.',
        },
        {
          title: 'Map real headers',
          body: 'Use header mapping to support different spreadsheet approaches without forcing one brittle template.',
        },
        {
          title: 'Keep results findable',
          body: 'Apply tags and project context so imported records can be filtered and reviewed later.',
        },
      ],
    },
    capabilities: [
      {
        title: 'Spreadsheet import',
        body: 'Support manual result workflows without leaving them in inboxes or shared folders.',
        items: ['CSV', 'XLSX', 'Manual evidence'],
      },
      {
        title: 'Header mapping',
        body: 'Adapt imported data to the FlowQA model even when teams use different column names.',
        items: ['Flexible columns', 'Mapped fields', 'Repeatable import'],
      },
      {
        title: 'Findable records',
        body: 'Use tags and project structure so manual results can be compared with other testing activity.',
        items: ['Tags', 'Projects', 'History'],
      },
    ],
    outcomes: [
      {
        label: 'Replace',
        value: 'Less email',
        body: 'Reduce spreadsheet forwarding and manual file archaeology.',
      },
      {
        label: 'Normalise',
        value: 'Flexible import',
        body: 'Bring different spreadsheet layouts into one reviewable structure.',
      },
      {
        label: 'Track',
        value: 'Searchable history',
        body: 'Make manual test results part of the same quality record as other sources.',
      },
    ],
    faq: [
      {
        question: 'Can teams keep their current spreadsheet format?',
        answer:
          'The aim is to support real-world CSV and XLSX structures through header mapping rather than forcing every team to start from scratch.',
      },
      {
        question: 'Does this remove manual testing?',
        answer: 'No. It makes manual test evidence easier to import, review, and track over time.',
      },
      {
        question: 'Can imported spreadsheets use tags?',
        answer: 'Yes. Tags are part of making imported results easier to filter and find later.',
      },
    ],
  },
  {
    slug: 'ai-generated-test-visibility',
    path: '/use-cases/ai-generated-test-visibility',
    title: 'AI-Generated Test Visibility | FlowQA',
    navLabel: 'AI Test Visibility',
    description:
      'Use FlowQA to understand AI-generated test volume by tracking what ran, what failed, and how generated tests affect quality history over time.',
    imageAlt: 'FlowQA visibility for AI-generated test results',
    eyebrow: 'Use case',
    hero: {
      title: 'Make AI-generated test volume understandable',
      summary:
        'As AI increases test-code throughput, FlowQA gives teams a clear view of what actually ran, what changed, and which results need attention.',
      primaryAction: sharedPrimaryAction,
      secondaryAction: sharedSecondaryAction,
    },
    problem: {
      title: 'More tests can create more noise',
      intro:
        'AI can help teams create tests quickly, but high output makes it harder to understand coverage, ownership, and result quality.',
      points: [
        {
          title: 'Volume rises fast',
          body: 'Generated tests can increase result counts faster than teams can manually review build output.',
        },
        {
          title: 'Ownership blurs',
          body: 'Teams need to understand where tests came from and which projects, suites, or tags they affect.',
        },
        {
          title: 'Noise hides value',
          body: 'Without result history, generated tests can add churn without making quality easier to understand.',
        },
      ],
    },
    solution: {
      title: 'FlowQA adds a review layer',
      intro:
        'Track generated and human-authored test output through the same quality history so teams can review the effect of increased test throughput.',
      points: [
        {
          title: 'See what ran',
          body: 'Keep generated test output visible as runs, suites, tests, and imported records rather than opaque job noise.',
        },
        {
          title: 'Measure the effect',
          body: 'Use quality metrics to understand whether generated tests are improving signal or creating recurring failure noise.',
        },
        {
          title: 'Filter with context',
          body: 'Use tags and project structure to group generated test results by purpose, owner, or risk area.',
        },
      ],
    },
    capabilities: [
      {
        title: 'Run visibility',
        body: 'Review generated test output through the same result history as other test sources.',
        items: ['Runs', 'Suites', 'Tests'],
      },
      {
        title: 'Quality signals',
        body: 'Understand whether increased test throughput is changing reliability or creating repeated failures.',
        items: ['Pass rate', 'Flake signals', 'Failure recurrence'],
      },
      {
        title: 'Context filters',
        body: 'Use tags and projects to keep generated test activity understandable across teams.',
        items: ['Tags', 'Projects', 'Result sources'],
      },
    ],
    outcomes: [
      {
        label: 'Control',
        value: 'Less noise',
        body: 'Give generated test output a product surface instead of leaving it buried in logs.',
      },
      {
        label: 'Review',
        value: 'Known impact',
        body: 'See whether added tests improve quality visibility or introduce recurring failures.',
      },
      {
        label: 'Scale',
        value: 'Shared context',
        body: 'Let teams adopt AI-assisted testing without losing result clarity.',
      },
    ],
    faq: [
      {
        question: 'Does FlowQA generate tests?',
        answer:
          'No. FlowQA tracks and reports test results. It helps teams understand the output from their existing test-generation and execution workflows.',
      },
      {
        question: 'Why does AI-generated testing need result history?',
        answer:
          'Higher test throughput makes trend visibility more important. Teams need to know whether added tests are producing useful signal or repeated noise.',
      },
      {
        question: 'Can generated tests be tagged?',
        answer:
          'Yes. Tags can help teams group generated results by source, project, risk area, or review workflow.',
      },
    ],
  },
] as const satisfies readonly PublicUseCasePageContent[];

export const publicUseCasePagesBySlug = new Map(
  publicUseCasePages.map((page) => [page.slug, page]),
);
